import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { workflowAPI } from '../../lib/api';
import { CHANNEL_META, CHANNEL_ORDER, CHANNEL_STATUS, errMsg } from '../../lib/workflow';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const ChannelDots = ({ channels, testId }) => (
  <div className="flex items-center gap-0.5" data-testid={testId}>
    {CHANNEL_ORDER.map((ch) => {
      const st = channels?.[ch]?.status || 'pending';
      return (
        <span
          key={ch}
          title={`${CHANNEL_META[ch].label}: ${CHANNEL_STATUS[st].label}`}
          className={`text-[12px] leading-none transition-opacity ${st === 'done' ? '' : st === 'in_progress' ? 'opacity-60' : 'opacity-25 grayscale'}`}
        >
          {CHANNEL_META[ch].emoji}
        </span>
      );
    })}
  </div>
);

const Chip = ({ ch, st, busy, ...rest }) => {
  const s = CHANNEL_STATUS[st.status] || CHANNEL_STATUS.pending;
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-sm ${s.cls}`}
    >
      <span aria-hidden>{CHANNEL_META[ch].emoji}</span>
      {CHANNEL_META[ch].label}
      {busy ? <Loader2 size={11} className="animate-spin" /> : <span aria-hidden>{s.emoji}</span>}
    </button>
  );
};

export const ChannelChips = ({ item, canEdit, onOpen, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const set = async (ch, status) => {
    setBusy(ch);
    try {
      const r = await workflowAPI.setChannel(item.id, ch, status);
      const label = CHANNEL_META[ch].label;
      toast.success(status === 'done' ? `${label} ticked off ✅` : status === 'skipped' ? `${label} skipped ⏭️` : `${label} reset ↩️`);
      if (r.data.stage === 'done' && item.stage !== 'done') toast.success('Every channel done. Listing complete 🎉');
      onChanged?.(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not update channel'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`channel-chips-${item.id}`}>
      {CHANNEL_ORDER.map((ch) => {
        const st = item.channels?.[ch] || { status: 'pending' };
        const testId = `utkarsh-channel-toggle-${ch}-${item.id}`;
        if (!canEdit) return <Chip key={ch} ch={ch} st={st} data-testid={testId} onClick={() => onOpen?.(ch)} />;
        return (
          <DropdownMenu key={ch}>
            <DropdownMenuTrigger asChild>
              <Chip ch={ch} st={st} busy={busy === ch} data-testid={testId} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 rounded-xl">
              <DropdownMenuLabel className="text-xs">
                {CHANNEL_META[ch].emoji} {CHANNEL_META[ch].label} · {CHANNEL_STATUS[st.status]?.label}
                {st.auto && <span className="block text-[10px] font-normal text-[#9A8E85]">auto-detected from the push panel</span>}
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onOpen?.(ch)} data-testid={`channel-open-${ch}-${item.id}`}>↗️ Open {CHANNEL_META[ch].label} panel</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => set(ch, 'done')} data-testid={`channel-done-${ch}-${item.id}`}>✅ Mark done</DropdownMenuItem>
              <DropdownMenuItem onClick={() => set(ch, 'skipped')} data-testid={`channel-skip-${ch}-${item.id}`}>⏭️ Skip (not needed)</DropdownMenuItem>
              {!st.auto && st.status !== 'pending' && (
                <DropdownMenuItem onClick={() => set(ch, 'pending')} data-testid={`channel-reset-${ch}-${item.id}`}>↩️ Reset</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </div>
  );
};
